import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MoreVertical, ShieldCheck, RefreshCw, Search, Filter } from 'lucide-react';
import { userRoleService } from '../../../../services/userRoleService'; 
import { UserRoles } from '../../../../types/userRole';
import './UserManagement.css';

const RoleList: React.FC = () => {
  const navigate = useNavigate();

  const [roles, setRoles] = useState<UserRoles[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const [filters, setFilters] = useState({
    role_id: '',
    is_active: ''
  });

  const [appliedFilters, setAppliedFilters] = useState({ ...filters });

  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 5,
    total: 0,
    total_pages: 1
  });

  const [stats, setStats] = useState({
    total_roles: 0,
    active_roles: 0,
    inactive_roles: 0
  });

  /* ================= FETCH ================= */
  useEffect(() => {
    fetchRoles();
    fetchStats();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoading(true);
      const data = await userRoleService.getUserRole();
      setRoles(data || []);

      setPagination(prev => ({
        ...prev,
        total: data.length,
        total_pages: Math.max(1, Math.ceil((data?.length || 0) / prev.page_size)),
        page: 1
      }));
    } catch (err) {
      console.error('Failed to load roles', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const [activeCount, inactiveCount, allRoles] = await Promise.all([
        userRoleService.getActiveRoleCount(),
        userRoleService.getInActiveRoleCount(),
        userRoleService.getUserRole()
      ]);

      setStats({
        total_roles: allRoles.length,
        active_roles: activeCount,
        inactive_roles: inactiveCount
      });
    } catch (err) {
      console.error('Failed to load stats', err);
    }
  };

  /* ================= FILTERS ================= */
  const applyFilters = () => {
    setAppliedFilters({ ...filters });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    const cleared = { role_id: '', is_active: '' };
    setFilters(cleared);
    setAppliedFilters(cleared);
    setSearch('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  /* ================= PAGINATION ================= */
  const filteredRoles = roles.filter(role => {
    const matchesSearch = role.name.toLowerCase().includes(search.toLowerCase());
    const matchesRole = !appliedFilters.role_id || role.id === Number(appliedFilters.role_id);
    const matchesStatus =
      !appliedFilters.is_active ||
      (appliedFilters.is_active === 'active' && role.is_active) ||
      (appliedFilters.is_active === 'inactive' && !role.is_active);
    return matchesSearch && matchesRole && matchesStatus;
  });

  const paginatedRoles = filteredRoles.slice(
    (pagination.page - 1) * pagination.page_size,
    pagination.page * pagination.page_size
  );

  const handlePageChange = (page: number) => {
    if (page < 1 || page > pagination.total_pages) return;
    setPagination(prev => ({ ...prev, page }));
  };

  /* ================= STATUS TOGGLE ================= */
  const handleToggleStatus = async (role: UserRoles) => {
    try {
      await userRoleService.updateRole(role.id, { is_active: !role.is_active });
     await fetchRoles();  
    await fetchStats();  
      setOpenMenuId(null);
    } catch (err) {
      console.error('Failed to update role status', err);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="user-management">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <h1>Roles</h1>
          <p>Manage system roles and permissions</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-outline" onClick={() => navigate('/admin/roles/access-matrix')}>
            <ShieldCheck size={18} /> Roles &amp; Access
          </button>
          <button className="btn btn-primary" onClick={() => navigate('/admin/roles/create')}>
            <Plus size={18} /> Add Role
          </button>
          <button className="btn btn-outline" onClick={fetchRoles}>
            <RefreshCw size={18} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-icon"><ShieldCheck size={24} /></div>
          <div className="stat-content">
            <h3>Total Roles</h3>
            <div className="stat-value">{stats.total_roles}</div>
          </div>
        </div>
        <div className="stat-card active">
          <div className="stat-icon"><ShieldCheck size={24} /></div>
          <div className="stat-content">
            <h3>Active Roles</h3>
            <div className="stat-value">{stats.active_roles}</div>
          </div>
        </div>
        <div className="stat-card inactive">
          <div className="stat-icon" style={{ color: 'red' }}><ShieldCheck size={24} /></div>
          <div className="stat-content">
            <h3>Inactive Roles</h3>
            <div className="stat-value">{stats.inactive_roles}</div>
          </div>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="controls-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by role"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="search-input"
            onKeyPress={e => { if(e.key === 'Enter') applyFilters(); }}
          />
          <button className="btn btn-outline btn-sm" onClick={applyFilters}>Search</button>
        </div>
        <div className="controls-right">
          <button className={`btn-filter ${showFilters ? 'active' : ''}`} onClick={() => setShowFilters(!showFilters)}>
            <Filter size={18} /> Filters
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label>Status</label>
              <select
                value={filters.is_active}
                onChange={e => setFilters(prev => ({ ...prev, is_active: e.target.value }))}
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="filter-actions">
              <button className="btn btn-outline" onClick={clearFilters}>Clear All</button>
              <button className="btn btn-primary" onClick={applyFilters}>Apply Filters</button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="table-container">
        {loading ? (
          <div className="loading-state">Loading roles...</div>
        ) : paginatedRoles.length === 0 ? (
          <div className="empty-state">No roles found</div>
        ) : (
          <>
            <table className="users-table role-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedRoles.map(role => (
                  <tr key={role.id}>
                    <td data-label="Role"><strong>{role.name}</strong></td>
                    <td data-label="Description">{role?.description || '-'}</td>
                    <td data-label="Status">
                      <span className={`badge ${role.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {role.is_active ? 'ACTIVE' : 'INACTIVE'}
                      </span>
                    </td>
                    <td data-label="Created At">{role.created_at ? new Date(role.created_at).toLocaleDateString() : '-'}</td>
                    <td data-label="Actions">
                      <div className="action-button">
                        <div className="dropdown">
                          <button className="btn-icon" onClick={() => setOpenMenuId(openMenuId === role.id ? null : role.id)}>
                            <MoreVertical size={16} />
                          </button>
                          {openMenuId === role.id && (
                            <div className="dropdown-menu">
                              <button className="dropdown-item" onClick={() => navigate(`/admin/roles/${role.id}/edit`)}>Edit</button>
                              <button
                                className={`dropdown-item ${role.is_active ? 'text-danger' : 'text-success'}`}
                                onClick={() => handleToggleStatus(role)}
                              >
                                {role.is_active ? 'Deactivate' : 'Activate'}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="pagination">
              <div style={{ display: "flex", alignItems: "center" }}>
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>

                <div className="pagination-pages">
                  {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.total_pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.total_pages - 2) {
                      pageNum = pagination.total_pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        className={`pagination-page ${pagination.page === pageNum ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.total_pages}
                >
                  Next
                </button>
              </div>

              <div className="pagination-info">
                Showing {(pagination.page - 1) * pagination.page_size + 1} to{' '}
                {Math.min(pagination.page * pagination.page_size, pagination.total)} of{' '}
                {pagination.total} roles
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RoleList;
